import {NullableStation, SpecialPriceMarket, Station} from "../../stations/station.service.interface";
import {NullableBoolean, NullableDate, NullableNumber, NullableString} from "../../../types/nullable-types";
import {FormModel} from "../../../models/forms/form.model";
import {FormFields, IFormField} from "../../../models/forms/form-field.interface";
import {IPassengerTypeSelectorsList} from "../../passenger-types/passengers-types.service.interface";
import {computed, IReactionDisposer, makeObservable, observable, reaction, runInAction} from "mobx";
import {FlightScheduleModel} from "../flights-schedule/flight-schedule.model";
import {VoidFlightsScheduleModel} from "../flights-schedule/void-flights-schedule.model";
import {IServiceFactory} from "../../service-factory.interface";
import {SessionStorageKeys} from "../../storage/session-storage-keys";
import {IFlightSearchControllerViewModel} from "./flight-search-controller-view-model.interface";
import {IFlightsScheduleViewModel} from "../flights-schedule/flights-schedule-view-model.interface";
import {IFlightScheduleFilter} from "../flights-schedule/flight-schedule-filter.interface";
import {
    IFlightSearchParams,
    SpecialPriceMarketUserAgreement,
    SpecialPriceMarketUserOptions
} from "./flight-search-params.interface";
import {WellKnownQueryParams} from "../../window/window.service.interface";
import {Check} from "../../../types/type-checking";

export class FlightSearchControllerModel extends FormModel<IFlightSearchParams> implements IFlightSearchControllerViewModel {
    constructor(services: IServiceFactory,
                private readonly _storageKey: SessionStorageKeys) {
        super(services);


        makeObservable<this, '_departureFlightSchedule' | '_returnFlightSchedule'>(this, {
            _departureFlightSchedule: observable.ref,
            _returnFlightSchedule: observable.ref,
            departureFlightSchedule: computed,
            returnFlightSchedule: computed,
        });
    }

    private _countPassengersFromUrl(): Record<string, number> {
        const result: Record<string, number> = {};
        this.services.passengerTypes.allPassengerTypeCodes.forEach(passengerType => {
            let passengerTypeCount = this.services.window.getUrlParam(passengerType);
            if(passengerTypeCount) {
                result[passengerType] = parseInt(passengerTypeCount);
            }
        });

        return result;
    }

    private _getSpecialPriceUserOptionQueryParam(): SpecialPriceMarketUserOptions {
        const value = this.services.window.getUrlParam(WellKnownQueryParams.SpecialPriceUserOption);
        if(Check.isNullOrUndefined(value)) {
            return SpecialPriceMarketUserOptions.None;
        }

        const parsedValue = parseInt(value);
        if(Check.isNumber(parsedValue)) {
            return parsedValue;
        }

        return SpecialPriceMarketUserOptions.None;
    }

    private _getSpecialPriceUserAgreementQueryParam(): SpecialPriceMarketUserAgreement {
        const value = this.services.window.getUrlParam(WellKnownQueryParams.SpecialPriceUserAgreement);
        if(Check.isNullOrUndefined(value)) {
            return SpecialPriceMarketUserAgreement.None;
        }

        const parsedValue = parseInt(value);
        if(Check.isNumber(parsedValue)) {
            return parsedValue;
        }

        return SpecialPriceMarketUserAgreement.None;
    }

    private _getPersistedSearchParams(): IPersistedSearchParams | null {
        const persistedValues = this.services.sessionStorage.getJson<IPersistedSearchParams>(this._storageKey);
        if(persistedValues) {
            return persistedValues;
        }

        const window = this.services.window;
        const isReturnFlight = window.getUrlParam(WellKnownQueryParams.IsRoundTripFlight)?.toLowerCase();
        return {
            departureOrigin: window.getUrlParam(WellKnownQueryParams.OriginStation),
            departureDestination: window.getUrlParam(WellKnownQueryParams.DestinationStation),
            departureDate: window.getUrlParam(WellKnownQueryParams.DepartureDate),
            returnDate: window.getUrlParam(WellKnownQueryParams.ReturnDate),
            passengers: this._countPassengersFromUrl(),
            isOneWayDepartureTrip:  isReturnFlight ? isReturnFlight !== "true" : null,
            isOneWayReturnTrip: null,
            returnOrigin: null,
            returnDestination: null,
            departureClassOfServices: null,
            returnClassOfServices: null,
            departureMinPrice: null,
            returnMinPrice: null,
            allowBogo: false,
            withBlueBenefits: false,
            specialPriceMarketUserOptions: this._getSpecialPriceUserOptionQueryParam(),
            specialPriceMarketUserAgreement: this._getSpecialPriceUserAgreementQueryParam(),
        }

    }

    protected _createFields(): FormFields<IFlightSearchParams> {
        const persistedFieldsValues = this._getPersistedSearchParams();
        const lang = this.services.language;
        return {
            isOneWayDepartureTrip: this._createField<boolean>({
                fieldName: () => 'Is one way',
                defaultValue: persistedFieldsValues?.isOneWayDepartureTrip ?? false,
            }),
            isOneWayReturnTrip: this._createField<boolean>({
                fieldName: () => 'Is one way',
                defaultValue: persistedFieldsValues?.isOneWayReturnTrip ?? false,
            }),
            departureOrigin: this._createField<Station>({
                fieldName: () => lang.translate('Departure'),
                isRequired: true,
                isRequiredErrorMessage: () => lang.translate('Select departure'),
                defaultValue: this.services.stations.tryGetStation(persistedFieldsValues?.departureOrigin)
            }),
            departureDestination: this._createField<Station>({
                fieldName: () => lang.translate('Destination'),
                isRequired: true,
                isRequiredErrorMessage: () => lang.translate('Select destination'),
                defaultValue: this.services.stations.tryGetStation(persistedFieldsValues?.departureDestination)
            }),
            specialPriceMarketUserOption: this._createField<SpecialPriceMarketUserOptions>({
               fieldName: () => "",
               isRequired: false,
               defaultValue: persistedFieldsValues?.specialPriceMarketUserOptions ?? SpecialPriceMarketUserOptions.None,
               isHidden: () => !this.supportsSpecialPriceMarket()
            }),
            specialPriceMarketUserAgreement: this._createField<SpecialPriceMarketUserAgreement>({
                fieldName: () => "",
                isRequired: false,
                defaultValue: persistedFieldsValues?.specialPriceMarketUserAgreement ?? SpecialPriceMarketUserAgreement.None,
                isHidden: () => !this.supportsSeasonSpecialPriceMarket()
            }),
            returnOrigin: this._createField<Station>({
                fieldName: () => lang.translate('Departure'),
                isRequired: false,
                defaultValue: this.services.stations.tryGetStation(persistedFieldsValues?.returnOrigin)
            }),
            returnDestination: this._createField<Station>({
                fieldName: () => lang.translate('Destination'),
                isRequired: false,
                defaultValue: this.services.stations.tryGetStation(persistedFieldsValues?.returnDestination)
            }),
            departureDate: this._createField<Date>({
                fieldName: () => lang.translate('Departure date'),
                isRequired: true,
                isRequiredErrorMessage: () => this.validateFlightDatesSelection(),
                defaultValue: this.services.time.tryConvertToDate(persistedFieldsValues?.departureDate)
            }),
            returnDate: this._createField<Date>({
                fieldName: () => lang.translate('Return date'),
                isRequired: () => !this.isOneWay,
                isRequiredErrorMessage: () => this.validateFlightDatesSelection(),
                defaultValue: this.services.time.tryConvertToDate(persistedFieldsValues?.returnDate)
            }),
            departureClassOfServices: this._createField<string[]>({
                fieldName: () => 'Class of services',
                isRequired: false,
                defaultValue: persistedFieldsValues?.departureClassOfServices || [],
            }),
            returnClassOfServices: this._createField<string[]>({
                fieldName: () => 'Class of services',
                isRequired: false,
                defaultValue: persistedFieldsValues?.returnClassOfServices || [],
            }),
            departureMinPrice: this._createField<NullableNumber>({
                fieldName: () => 'Minimum price',
                isRequired: false,
                defaultValue: persistedFieldsValues?.departureMinPrice || null,
            }),
            returnMinPrice: this._createField<NullableNumber>({
                fieldName: () => 'Minimum price',
                isRequired: false,
                defaultValue: persistedFieldsValues?.returnMinPrice || null,
            }),
            withBlueBenefits: this._createField<boolean>({
                fieldName: () => '',
                isRequired: false,
                defaultValue: false,
            }),
            passengers: this._createField<IPassengerTypeSelectorsList>({
                fieldName: () => lang.translate('Passengers'),
                defaultValue: this.services.passengerTypes.getPassengersSelectors(() => this.withBlueBenefits, persistedFieldsValues?.passengers ?? {}),
                validate: () => {
                    if(this.passengers.countAllWithoutInfants() <= 0) {
                        return this.services.language.translate('You must select at least one passenger')
                    }
                    return null;
                }
            }),
            allowBogo: this._createField<boolean>({
                fieldName: () => 'Allow Bogo',
                isRequired: false,
                defaultValue: Boolean(persistedFieldsValues?.allowBogo),
            }),
            departureFlightScheduleFilter: this._createField<IFlightScheduleFilter>({
                fieldName: () => 'Departure flight schedule filter',
                isRequired: false
            }),
            returnFlightScheduleFilter: this._createField<IFlightScheduleFilter>({
                fieldName: () => 'Return flight schedule filter',
                isRequired: false
            })
        };
    }

    getSpecialPriceMarket(): SpecialPriceMarket | null {
        return this.services.stations.getSpecialPriceMarket(this.departureOrigin, this.departureDestination);
    }

    supportsSpecialPriceMarket(): boolean {
        return Boolean(this.getSpecialPriceMarket());
    }

    supportsSeasonSpecialPriceMarket(): boolean {
        return Boolean(this.getSpecialPriceMarket()?.activeSeason);
    }

    get availableDepartureStations(): Station[] {
        return this.services.stations.getAllStations();
    }

    get availableReturnStations(): Station[] {
        if(this.isOneWayReturnTrip) {
            if(this.returnOrigin) {
                return this.returnOrigin.markets;
            } else {
                return [];
            }
        }

        if(this.departureOrigin) {
            return this.departureOrigin.markets;
        }
        return [];
    }

    get recentlySearchedOrigins(): Station[] {
        return this.services.flightSearch.getRecentlySearchedOrigins();
    }

    get recentlySearchedDestinations(): Station[] {
        const stations = this.services.flightSearch.getRecentlySearchedDestinations();
        if(this.isOneWayReturnTrip) {
            return stations.filter(s => s.stationCode !== this.returnOrigin?.stationCode)
        } else {
            return stations.filter(s => s.stationCode !== this.departureOrigin?.stationCode)
        }
    }

    get isOneWay(): boolean {
        return this.isOneWayDepartureTrip || this.isOneWayReturnTrip;
    }

    get isOneWayDepartureTrip(): boolean {
        return this.fields.isOneWayDepartureTrip.value || false;
    }

    set isOneWayDepartureTrip(value: boolean) {
        if(value) {
            this.fields.isOneWayReturnTrip.setValue(false);
        }
        this.fields.isOneWayDepartureTrip.setValue(value);
    }

    get isOneWayReturnTrip(): boolean {
        return this.fields.isOneWayReturnTrip.value || false;
    }

    set isOneWayReturnTrip(value: boolean) {
        if(value) {
            this.fields.isOneWayDepartureTrip.setValue(false);
        }
        this.fields.isOneWayReturnTrip.setValue(value);
    }

    get withBlueBenefits(): boolean {
        return Boolean(this.fields.withBlueBenefits.value);
    }

    set withBlueBenefits(value: boolean) {
        this.fields.withBlueBenefits.setValue(value);
    }

    get departureOrigin(): NullableStation {
        if(this.isOneWayReturnTrip) {
            return null;
        }
        return this.fields.departureOrigin.value;
    }

    set departureOrigin(value: NullableStation) {
        this.fields.departureOrigin.setValue(value);
    }

    get departureDestination(): NullableStation {
        if(this.isOneWayReturnTrip) {
            return null;
        }

        return this.fields.departureDestination.value;
    }
    set departureDestination(value: NullableStation) {
        this.fields.departureDestination.setValue(value);
    }

    get returnOrigin(): NullableStation {
        if(this.isOneWayDepartureTrip) {
            return null;
        }
        return this.fields.returnOrigin.value || this.departureDestination;
    }

    set returnOrigin(value: NullableStation) {
        this.fields.returnOrigin.setValue(value);
    }

    get returnDestination(): NullableStation {
        if(this.isOneWayDepartureTrip) {
            return null;
        }

        return this.fields.returnDestination.value || this.departureOrigin;
    }

    set returnDestination(value: NullableStation) {
        this.fields.returnDestination.setValue(value);
    }

    get departureDate(): NullableDate {
        if(this.isOneWayReturnTrip) {
            return null;
        }
        return this.fields.departureDate.value;
    }

    set departureDate(value: NullableDate) {
        this.fields.departureDate.setValue(value);
    }

    get returnDate(): NullableDate {
        if(this.isOneWayDepartureTrip) {
            return null;
        }
        return this.fields.returnDate.value;
    }

    set returnDate(value: NullableDate) {
        this.fields.returnDate.setValue(value);
    }

    get passengers(): IPassengerTypeSelectorsList {
        return this.fields.passengers.value!
    }

    set passengers(value: IPassengerTypeSelectorsList) {
        this.fields.passengers.setValue(value);
    }

    get departureClassOfServices(): string[] {
        return this.fields.departureClassOfServices.value || [];
    }

    set departureClassOfServices(value: string[]) {
        this.fields.departureClassOfServices.setValue(value);
    }

    get returnClassOfServices(): string[] {
        return this.fields.returnClassOfServices.value || [];
    }

    set returnClassOfServices(value: string[]) {
        this.fields.returnClassOfServices.setValue(value);
    }

    get departureMinPrice(): NullableNumber {
        return this.fields.departureMinPrice.value;
    }

    set departureMinPrice(value: NullableNumber) {
        this.fields.departureMinPrice.setValue(value);
    }

    get returnMinPrice(): NullableNumber {
        return this.fields.returnMinPrice.value;
    }

    set returnMinPrice(value: NullableNumber) {
        this.fields.returnMinPrice.setValue(value);
    }

    get allowBogo(): boolean {
        return Boolean(this.fields.allowBogo.value);
    }

    set allowBogo(value: boolean) {
        this.fields.allowBogo.setValue(value);
    }

    get departureFlightScheduleFilter(): IFlightScheduleFilter | null {
        return this.fields.departureFlightScheduleFilter.value;
    }

    set departureFlightScheduleFilter(value: IFlightScheduleFilter | null) {
        this.fields.departureFlightScheduleFilter.setValue(value);
    }

    get returnFlightScheduleFilter(): IFlightScheduleFilter | null {
        return this.fields.returnFlightScheduleFilter.value;
    }

    set returnFlightScheduleFilter(value: IFlightScheduleFilter | null) {
        this.fields.returnFlightScheduleFilter.setValue(value);
    }

    validateFlightDatesSelection(): string {


        if(this.isOneWay) {
            if(!this.departureDate) {
                return this.services.language.translate('Select the departure date');
            }
        } else {
            if(!this.departureDate || !this.returnDate) {
                return this.services.language.translate('Select the flights dates');
            }
        }

        return "";
    }

    private _reactions: IReactionDisposer[] = [];

    private _resetUseSpecialPriceMarketSelection(): void {
        this.fields.specialPriceMarketUserOption.setValue(SpecialPriceMarketUserOptions.None);
        this.fields.specialPriceMarketUserAgreement.setValue(SpecialPriceMarketUserAgreement.None);

    }

    protected _onFieldsCreated(fields: FormFields<IFlightSearchParams>) {
        super._onFieldsCreated(fields);

        fields.departureOrigin.onChange(() => {
            this._resetDates();
            if(fields.departureDestination.value !== null) {
                fields.departureDestination.setValue(null);
            } else {
                this._resetFlightsSchedule();
            }

            this._resetUseSpecialPriceMarketSelection();
        });

        fields.departureDestination.onChange(() => {
            this._resetDates();
            this._resetFlightsSchedule();
            this._resetUseSpecialPriceMarketSelection();
        });

        fields.specialPriceMarketUserOption.onChange(() => {
            this._resetFlightsSchedule();

        });

        fields.specialPriceMarketUserAgreement.onChange(() => {
            this._resetFlightsSchedule();
        });


        fields.returnOrigin.onChange(() => {
            this._resetDates();
            if(fields.returnDestination.value !== null) {
                fields.returnDestination.setValue(null);
            } else {
                this._resetFlightsSchedule();
            }

            this._resetUseSpecialPriceMarketSelection();
        });

        fields.returnDestination.onChange(() => {
            this._resetDates();
            this._resetFlightsSchedule();
            this._resetUseSpecialPriceMarketSelection();
        });

        fields.departureFlightScheduleFilter.onChange(() => {
            this._resetFlightsSchedule();
        });

        fields.returnFlightScheduleFilter.onChange(() => {
            this._resetFlightsSchedule();
        })


        this._reactions.push(reaction(() =>  fields.passengers.value?.getAll().map(p => p.count),
                                        () => this._persistFieldsValues()));



    }

    private _resetDates(): void {
        this.fields.departureDate.setValue(null);
        this.fields.returnDate.setValue(null);
    }

    protected _onFieldChanged(field: IFormField) {
        super._onFieldChanged(field);
        this._persistFieldsValues();
    }

    private _resetFlightsSchedule(): void {
        runInAction(() => {
            this._departureFlightSchedule = null;
            this._returnFlightSchedule = null;
        })
    }


    private _departureFlightSchedule: IFlightsScheduleViewModel | null = null;
    get departureFlightSchedule(): IFlightsScheduleViewModel {
        return this._getFlightSchedule(this.departureOrigin,
            this.departureDestination,
            () => null,
            () => this._departureFlightSchedule,
            schedule => this._departureFlightSchedule = schedule,
            this.departureFlightScheduleFilter);
    }

    private _getMinAllowedReturnDate(): NullableDate {

        if(this.isOneWay) {
            return null;
        }

        return this.departureDate;

    }
    private _returnFlightSchedule: IFlightsScheduleViewModel | null = null;
    get returnFlightSchedule(): IFlightsScheduleViewModel {
        return this._getFlightSchedule(
            this.returnOrigin,
            this.returnDestination,
            () =>  this._getMinAllowedReturnDate(),
            () => this._returnFlightSchedule,
            schedule => this._returnFlightSchedule = schedule,
            this.returnFlightScheduleFilter);
    }

    private _getFlightSchedule(origin: NullableStation,
                               destination: NullableStation,
                               getMinAllowedDate: () => NullableDate,
                               getCurrentSchedule: () => IFlightsScheduleViewModel | null,
                               setCurrentSchedule: (schedule: IFlightsScheduleViewModel) => void,
                               scheduleFilter: IFlightScheduleFilter | null): IFlightsScheduleViewModel{

        const currentSchedule = getCurrentSchedule();
        if(currentSchedule) {
            return currentSchedule;
        }

        if(!origin || !destination) {
            return new VoidFlightsScheduleModel();
        }

        if(origin.stationCode === destination.stationCode) {
            return new VoidFlightsScheduleModel();
        }

        const newSchedule = new FlightScheduleModel(this.services, origin, destination, getMinAllowedDate, scheduleFilter);

        runInAction(() => {
            setCurrentSchedule(newSchedule);
        });

        return newSchedule;
    }


    private  _persistFieldsValues(): void {

        const persistedFieldValues: IPersistedSearchParams = {
            isOneWayDepartureTrip: this.fields.isOneWayDepartureTrip.value,
            isOneWayReturnTrip: this.fields.isOneWayReturnTrip.value,
            withBlueBenefits: this.fields.withBlueBenefits.value,
            departureOrigin: this.fields.departureOrigin.value?.stationCode || null,
            departureDestination: this.fields.departureDestination.value?.stationCode || null,
            returnOrigin: this.fields.returnOrigin.value?.stationCode || null,
            returnDestination: this.fields.returnDestination.value?.stationCode || null,
            departureDate: this.services.time.formatYYY_MM_DD(this.fields.departureDate.value),
            returnDate: this.services.time.formatYYY_MM_DD(this.fields.returnDate.value),
            passengers: this._serializePassengers(),
            departureClassOfServices: this.fields.departureClassOfServices.value,
            returnClassOfServices: this.fields.returnClassOfServices.value,
            departureMinPrice: this.fields.departureMinPrice.value,
            returnMinPrice: this.fields.returnMinPrice.value,
            allowBogo: this.allowBogo,
            specialPriceMarketUserOptions: this.fields.specialPriceMarketUserOption.value,
            specialPriceMarketUserAgreement: this.fields.specialPriceMarketUserAgreement.value
        }

        this.services.sessionStorage.setJson(this._storageKey, persistedFieldValues);
    }

    private _serializePassengers(): Record<string, number> {
        if(this.fields.passengers.value) {
            return this.fields.passengers.value.getAll().toDictionaryOfType(item => item.getCurrentCode(), item => item.count);
        } else {
            return {}
        }
    }

    dispose(): void {
        this.services.sessionStorage.removeItem(this._storageKey);
        this._reactions.forEach(disposer => disposer());
        this._reactions = [];
    }

}


interface IPersistedSearchParams {
    isOneWayDepartureTrip: NullableBoolean;
    isOneWayReturnTrip: NullableBoolean;
    withBlueBenefits: NullableBoolean;
    departureOrigin: NullableString;
    departureDestination: NullableString;
    returnOrigin: NullableString,
    returnDestination: NullableString;
    departureDate: NullableString;
    returnDate: NullableString;
    passengers: Record<string, number>;
    departureClassOfServices: string[] | null;
    returnClassOfServices: string[] | null;
    departureMinPrice: NullableNumber;
    returnMinPrice: NullableNumber;
    allowBogo: boolean;
    specialPriceMarketUserOptions: SpecialPriceMarketUserOptions | null;
    specialPriceMarketUserAgreement: SpecialPriceMarketUserAgreement | null;
}
